import { useState } from 'react'
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from '@reach/router'

import useMutation from 'hooks/useMutation'
import useQuery from 'hooks/useQuery'
import { IncreaseCheckoutLite } from './IncreaseCheckoutLite'
import { TaxIdentity } from './TaxIdentity'
import PlanSummary from '../../Planes/PlanSummary'
import { planName } from '../../Planes/planDisplay'
import Blocked from './Blocked'
import useSubscriptionPlan from './useSubscriptionPlan'
import { FIELDS, normalizePhone, validateSubscription } from './validation'
import styles from './styles.module.scss'
let clone = window.structuredClone || JSON.clone

// Suscripción abierta en la lista de /subscriptions, con el mismo motivo que daría
// subscription-check: pendiente → terminarla en su checkout; activa o pausada → ya existe.
// Para cuando el chequeo no responde (review de Lucio en #389).
const deniedFromSubscriptions = items => {
  if (!Array.isArray(items)) return null
  let pending = items.find(item => item.status === 'pending')
  if (pending) return { reason: 'subscription_exists', checkoutLink: pending.checkout_link }
  if (items.some(item => item.status === 'active' || item.status === 'paused')) return { reason: 'subscription_exists' }
  return null
}

let {
  REACT_APP_INCREASE_PLAN_ID
} = process.env

export function Form(props) {
  let { t } = useTranslation();
  let [subscription, setSubscription] = useState({ paymentMethod: 'card' })
  // Campos que el usuario ya dejó: se marcan en rojo apenas quedan mal, sin esperar a
  // "Siguiente" (que está deshabilitado hasta confirmar el CUIT).
  let [touched, setTouched] = useState({})
  let [taxInfo, setTaxInfo] = useState(null)
  let [saveFailed, setSaveFailed] = useState(false)
  let [mutate, mutation] = useMutation(window._l(`/subscriptions`).substr(1)); 
  let [view, setView] = useState('loading')
  let navigate = useNavigate();
  // /suscripcion/<plan> (pro, premium, esencial): el link que reenvía Ventas (card 1053,
  // caso 5). Suma el panel del plan; el alta es la misma. Un plan que no está en el
  // catálogo manda a la pantalla de Planes.
  let planSlug = props.plan
  let [existing, setExisting] = useState(null)

  useQuery(window._l('/subscriptions').slice(1), {
    onSuccess: items => {
      // Con /<plan> esto lo decide subscription-check (useSubscriptionPlan); la lista queda por
      // si el chequeo falla.
      if (planSlug) setExisting(items)
      if (planSlug || !REACT_APP_INCREASE_PLAN_ID || !Array.isArray(items)) {
        setView('init')
        return
      }

      let hash = {}
      for (let { checkout_link, status } of items) {
        hash[status] ||= checkout_link
      }

      if (hash.active) {
        navigate(window._l(`/events/agenda`))
      } else if (hash.pending) {
        window.location.href = hash.pending
      } else {
        setView('init')
      }
    }
  })

  // Después de /subscriptions: el plan de la ruta no cambia esa consulta.
  let { plan, planCode, loading: planLoading, denied: planDenied, checkFailed } = useSubscriptionPlan(planSlug)
  // Sólo antes del alta: después, la suscripción recién creada no es motivo para ocultar el pago.
  let denied = subscription.id ? null : planDenied || (checkFailed ? deniedFromSubscriptions(existing) : null)
  let planLabel = plan ? planName(t, plan) : ''

  let fieldWarnings = validateSubscription(subscription, t)
  let warnings = {}
  for (let key of FIELDS) {
    if (touched[key] && fieldWarnings[key]) warnings[key] = fieldWarnings[key]
  }
  let touch = key => () => setTouched(prev => (prev[key] ? prev : { ...prev, [key]: true }))

  let onChangeSubscription = (key, value) => {
    let payload = clone(subscription)
    payload[key] = value
    setSubscription(payload)
  }

  let onUpdatePaymentMethod = status => {
    if (status === 'PAYMENT_SUCCESS') {
      setView('ready')
    } else if (status === 'PAYMENT_ERROR') {

    } else if (status === 'PAYMENT_IN_PROCESS') {

    } else if (status === 'LOAD_ERROR') {

    } else if (status === 'LOAD_SUCCESS') {

    }
  }

  let save = async () => {
    let { paymentMethod = 'card' } = subscription

    setTouched(Object.fromEntries(FIELDS.map(key => [key, true])))
    if (Object.keys(fieldWarnings).length) return

    setSaveFailed(false)
    // Con el plan de la ruta el api lo valida (y el tope de agendas) y se lo asigna al team.
    mutate({ ...subscription, ...(subscription.phone && { phone: normalizePhone(subscription.phone) }), taxID: taxInfo.cuit, ...(planCode && { planCode }) }, {
      onSuccess: data => {
        if (paymentMethod === 'cbu') {
          setView('ready')
        } else {
          setView(paymentMethod)
        }
        setSubscription(data)
      },
      // Se queda en el formulario para reintentar sin perder lo cargado ni el CUIT confirmado.
      onError: error => {
        console.error('[subscriptions] save failed', error?.message)
        setSaveFailed({ reason: error?.code, checkoutLink: error?.data?.checkoutLink })
      }
    })
  }

  return (
    <div style={{ top: 0, left: 0, paddingTop: '80px' }} className="position-fixed overflow-auto bg-white w-100 h-100 px-4 pb-4">
      <div style={{ opacity: mutation.isLoading ? .5 : 1 }}>
        {view === 'loading' || planLoading ? (
          <div className="text-center mt-3"><span className="spinner-border spinner-border text-primary" style={{ width: '3rem', height: '3rem' }} role="status"></span></div>
        ) : denied ? (
          <div style={{ maxWidth: 560, width: '100%' }} className="mx-auto">
            <h5>{t('subscriptions_payment_method_form_title', 'Formulario de suscripcion')}</h5>
            <Blocked reason={denied.reason} plan={planLabel} checkoutLink={denied.checkoutLink} />
          </div>
        ) : view === 'ready' ? (
          <div style={{ maxWidth: 480, width: '100%' }} className="mx-auto text-center">
            <div className="alert alert-success">{t('subscription_create_success', 'Los datos fueron guardados correctamente')}</div>
            <div><Link to={window._l(`/events/agenda`)}>{t('subscriptions_goto_agenda', 'Ir a la Agenda')}</Link></div>
          </div>
        ) : !!subscription?.id && view === 'card' ? (
          <div style={{ maxWidth: 1024 }} className="mx-auto">
            <h5>{t('subscriptions_payment_method_form_title', 'Formulario de suscripcion')}</h5>
            <IncreaseCheckoutLite onUpdate={onUpdatePaymentMethod} subscriptionID={subscription?.id} />
          </div>
        ) : view === 'init' && !!subscription && (
          <div style={{ maxWidth: planCode ? 1240 : 1024 }} className={`mx-auto ${planCode ? styles.withPlan : ''}`}>
          <div className={planCode ? styles.formCard : undefined}>
            <h5>{t('subscriptions_payment_method_form_title', 'Formulario de suscripcion')}</h5>
            <div>
              <div className="my-3">
                <TaxIdentity onChange={setTaxInfo} />
              </div>

              <div className="d-lg-flex align-items-top gap-3">
                <div className="col-lg">
                  <label htmlFor={`billingLastName`} className="form-label text-nowrap mt-3 mt-lg-0">{t('billing_last_name', 'Apellido')}</label>
                  <input
                    type="text"
                    className={`form-control ${warnings.lastName ? 'is-invalid' : ''}`}
                    id={`billingLastName`}
                    value={subscription.lastName || subscription.customer?.last_name || ''}
                    onChange={e => onChangeSubscription('lastName', e.target.value)}
                    onBlur={touch('lastName')}
                  />
                  {warnings.lastName && (<div className="invalid-feedback">{warnings.lastName}</div>)}
                </div>
                <div className="col-lg">
                  <label htmlFor={`billingFirstName`} className="form-label text-nowrap mt-3 mt-lg-0">{t('billing_first_name', 'Nombre')}</label>
                  <input
                    type="text"
                    className={`form-control ${warnings.firstName ? 'is-invalid' : ''}`}
                    id={`billingFirstName`}
                    value={subscription.firstName || subscription.customer?.first_name || ''}
                    onChange={e => onChangeSubscription('firstName', e.target.value)}
                    onBlur={touch('firstName')}
                  />
                  {warnings.firstName && (<div className="invalid-feedback">{warnings.firstName}</div>)}
                </div>
              </div>

              <div className="d-lg-flex align-items-top gap-3 my-3">
                <div className="col-lg">
                  <label htmlFor={`billingAddressLine1`} className="form-label text-nowrap mt-3 mt-lg-0">{t('billing_address_line_1', 'Linea direccion 1')}</label>
                  <input
                    type="text"
                    className={`form-control ${warnings.addressLine1 ? 'is-invalid' : ''}`}
                    id={`billingAddressLine1`}
                    value={subscription.addressLine1 || subscription.customer?.billing_info?.address_line_1 || ''}
                    onChange={e => onChangeSubscription('addressLine1', e.target.value)}
                    onBlur={touch('addressLine1')}
                  />
                  {warnings.addressLine1 && (<div className="invalid-feedback">{warnings.addressLine1}</div>)}
                </div>
                <div className="col-lg">
                  <label htmlFor={`billingAddressStateCode`} className="form-label text-nowrap mt-3 mt-lg-0">{t('billing_address_state_code', 'Provincia')}</label>
                  <select
                    className={`form-select ${warnings.stateCode ? 'is-invalid' : ''}`}
                    id={`billingAddressStateCode`}
                    value={subscription.stateCode || subscription.customer?.billing_info?.state_iso_code|| 'AR-C'}
                    onChange={e => onChangeSubscription('stateCode', e.target.value)}
                  >
                    <option value="AR-B">Buenos Aires </option>
                    <option value="AR-C">Ciudad Autónoma de Buenos Aires</option>
                    <option value="AR-K">Catamarca</option>
                    <option value="AR-H">Chaco</option>
                    <option value="AR-U">Chubut </option>
                    <option value="AR-X">Córdoba</option>
                    <option value="AR-W">Corrientes </option>
                    <option value="AR-E">Entre Ríos </option>
                    <option value="AR-P">Formosa</option>
                    <option value="AR-Y">Jujuy</option>
                    <option value="AR-L">La Pampa </option>
                    <option value="AR-F">La Rioja </option>
                    <option value="AR-M">Mendoza</option>
                    <option value="AR-N">Misiones </option>
                    <option value="AR-Q">Neuquén</option>
                    <option value="AR-R">Río Negro</option>
                    <option value="AR-A">Salta</option>
                    <option value="AR-J">San Juan </option>
                    <option value="AR-D">San Luis </option>
                    <option value="AR-Z">Santa Cruz </option>
                    <option value="AR-S">Santa Fe </option>
                    <option value="AR-G">Santiago del Estero</option>
                    <option value="AR-V">Tierra del Fuego </option>
                    <option value="AR-T">Tucumán</option>
                  </select>
                  {warnings.stateCode && (<div className="invalid-feedback">{warnings.stateCode}</div>)}
                </div>

                <div className="col-lg">
                  <label htmlFor={`billingAddressCity`} className="form-label text-nowrap mt-3 mt-lg-0">{t('billing_address_city', 'Ciudad')}</label>
                  <input
                    type="text"
                    className={`form-control ${warnings.city ? 'is-invalid' : ''}`}
                    id={`billingAddressCity`}
                    value={subscription.city || subscription.customer?.billing_info?.city || ''}
                    onChange={e => onChangeSubscription('city', e.target.value)}
                    onBlur={touch('city')}
                  />
                  {warnings.city && (<div className="invalid-feedback">{warnings.city}</div>)}
                </div>
              </div>

              <div className="d-lg-flex align-items-top gap-3">
                <div className="col-lg">
                  <label htmlFor={`billingAddressZipCode`} className="form-label text-nowrap mt-3 mt-lg-0">{t('billing_address_zip_code', 'Codigo Postal')}</label>
                  <input
                    type="text"
                    className={`form-control ${warnings.zipCode ? 'is-invalid' : ''}`}
                    id={`billingAddressZipCode`}
                    value={subscription.zipCode || subscription.customer?.billing_info?.zip_code || ''}
                    onChange={e => onChangeSubscription('zipCode', e.target.value)}
                    onBlur={touch('zipCode')}
                  />
                  {warnings.zipCode && (<div className="invalid-feedback">{warnings.zipCode}</div>)}
                </div>

                <div className="col-lg">
                  <label htmlFor={`billingEmail`} className="form-label text-nowrap mt-3 mt-lg-0">{t('billing_email', 'Email')}</label>
                  <input
                    type="email"
                    className={`form-control ${warnings.email ? 'is-invalid' : ''}`}
                    id={`billingEmail`}
                    value={subscription.email || subscription.customer?.email || ''}
                    onChange={e => onChangeSubscription('email', e.target.value)}
                    onBlur={touch('email')}
                  />
                  {warnings.email && (<div className="invalid-feedback">{warnings.email}</div>)}
                </div>

                <div className="col-lg">
                  <label htmlFor={`billingPhone`} className="form-label text-nowrap mt-3 mt-lg-0">{t('billing_phone', 'Telefono')}</label>
                  <input
                    type="text"
                    className={`form-control ${warnings.phone ? 'is-invalid' : ''}`}
                    id={`billingPhone`}
                    value={subscription.phone || subscription.customer?.billing_info?.phone || ''}
                    onChange={e => onChangeSubscription('phone', e.target.value)}
                    onBlur={touch('phone')}
                  />
                  {warnings.phone && (<div className="invalid-feedback">{warnings.phone}</div>)}
                  <small className="text-secondary">{t("billing_phone_hint", "Ingresar números sin espacios ni guiones. Sin 0 ni 15")}</small>
                </div>
              </div>

              <div className="mt-3">
                <div>
                  <div className="form-check-inline fw-bold">
                    {t('subscriptions_payment_method_long', 'Medio pago')}
                  </div>
                  <div className="form-check form-check-inline">
                    <input
                      className="form-check-input"
                      onChange={() => onChangeSubscription('paymentMethod', 'card')}
                      checked={subscription.paymentMethod === 'card'}
                      type="radio"
                      name="subscriptionPaymentMethod"
                      id="subscriptionPaymentMethod1"
                    />
                    <label className="form-check-label" htmlFor="subscriptionPaymentMethod1">
                      {t('subscriptions_payment_method_card', 'Tarjeta de credito')}
                    </label>
                  </div>
                  <div className="form-check form-check-inline">
                    <input
                      className="form-check-input"
                      onChange={() => onChangeSubscription('paymentMethod', 'cbu')}
                      checked={subscription.paymentMethod === 'cbu'}
                      type="radio"
                      name="subscriptionPaymentMethod"
                      id="subscriptionPaymentMethod2"
                    />
                    <label className="form-check-label" htmlFor="subscriptionPaymentMethod2">
                      {t('subscriptions_payment_method_cbu', 'Debito en cuenta - CBU')}
                    </label>
                  </div>
                  <div className={`d-inline-block ${subscription.paymentMethod === 'cbu' ? '' : 'invisible'}`}>
                    {/* Bug real (auditoría 2026-08-18, responsive): el input
                        de CBU tiene width y minWidth fijos en 265px (nunca se
                        achica), en una fila sin flex-wrap junto al mensaje de
                        error de validación -- con ~327px disponibles en el
                        form a 375px, el margen para el mensaje de error es
                        muy ajustado. flex-wrap deja que el mensaje pase a su
                        propia línea en vez de arriesgarse a que el
                        overflow-x:hidden global lo recorte. */}
                    <div className="d-flex flex-wrap gap-3 align-items-center">
                      <input
                        type="text"
                        className={`form-control ${warnings.cbu ? 'is-invalid' : ''}`}
                        id={`cbu`}
                        style={{ width: '265px', minWidth: '265px' }}
                        value={subscription.cbu || ''}
                        placeholder={subscription.payment_method?.data?.number || t('cbu', 'Nro. CBU')}
                        onChange={e => onChangeSubscription('cbu', e.target.value.replace(/\D/g, ''))}
                        onBlur={touch('cbu')}
                      />
                      {warnings.cbu && (<div className="invalid-feedback">{warnings.cbu}</div>)}
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div>
              {saveFailed && <Blocked reason={saveFailed.reason} plan={planLabel} checkoutLink={saveFailed.checkoutLink} />}
              {!taxInfo && (
                <div className="text-secondary small mt-3">{t('subscriptions_form_tax_id_not_confirmed', 'Validá y confirmá el CUIT para continuar')}</div>
              )}
              <button type="button" disabled={mutation.isLoading || !taxInfo} onClick={save} className="btn btn-primary text-first-up">
                {mutation.isLoading && (
                  <span className="spinner-border spinner-border-sm" role="status"></span>
                )} {t('next', 'siguiente')}
              </button>
            </div>
          </div>
          {planCode && <PlanSummary code={planCode} />}
          </div>
        )}
      </div>
    </div>
  )
}
